<template>
  <div>
    <div class="main-container" v-if="!hasError">
      <template v-if="!isBuildLoading">
        <!-- content section -->
        <div class="content-section">
          <BreadCrumbs
            v-if="shouldShowBreadcrumbs && breadcrumbItems.length > 0"
            :items="breadcrumbItems"
            class="desktop-only"
          />
          <div class="header-wrapper">
            <h1 class="header">{{ title }}</h1>
          </div>
          <div class="border-header"></div>
          <div class="lesson-meta-container">
            <div class="lesson-meta">
              <div class="lesson-meta-item">
                <div class="lesson-meta-item-icon">
                  <img src="/images/ai-assisted.svg" alt="ai-assisted" />
                </div>
                <span class="meta-text meta-text-blue">AI Assisted</span>
              </div>
              <v-divider
                vertical
                class="mobile-divider desktop-only"
              ></v-divider>
              <div
                v-if="
                  (Array.isArray(softwareName) && softwareName.length > 0) ||
                  (typeof softwareName === 'string' && softwareName.trim())
                "
                class="lesson-meta-item software-item"
              >
                <div class="lesson-meta-item-icon">
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M17 22.75H7C6.59 22.75 6.25 22.41 6.25 22C6.25 21.59 6.59 21.25 7 21.25H17C17.41 21.25 17.75 21.59 17.75 22C17.75 22.41 17.41 22.75 17 22.75Z"
                      fill="#E00069"
                    />
                    <path
                      d="M18 2H6C3.79 2 2 3.79 2 6V15C2 17.21 3.79 19 6 19H18C20.21 19 22 17.21 22 15V6C22 3.79 20.21 2 18 2ZM9.99 7.32C10.55 7.32 11 7.77 11 8.32C11 8.87 10.55 9.32 10 9.32C9.45 9.32 9 8.87 9 8.32C9 7.77 9.44 7.32 9.99 7.32ZM6.84 7.32C7.4 7.32 7.85 7.77 7.85 8.32C7.85 8.87 7.4 9.32 6.85 9.32C6.3 9.32 5.85 8.87 5.85 8.32C5.85 7.77 6.28 7.32 6.84 7.32ZM17.26 14.86H6.76C6.34 14.86 6 14.52 6 14.11C6 13.7 6.33 13.36 6.74 13.36H17.27C17.68 13.36 18.02 13.7 18.02 14.11C18.02 14.52 17.68 14.86 17.26 14.86ZM17.26 9.07H13.58C13.17 9.07 12.83 8.73 12.83 8.32C12.83 7.91 13.17 7.57 13.58 7.57H17.26C17.67 7.57 18.01 7.91 18.01 8.32C18.01 8.73 17.68 9.07 17.26 9.07Z"
                      fill="#E00069"
                    />
                  </svg>
                </div>
                <span class="meta-text meta-text-pink">
                  Software:
                  {{
                    Array.isArray(softwareName)
                      ? softwareName.join(", ")
                      : softwareName
                  }}
                </span>
              </div>
              <v-divider
                v-if="
                  (Array.isArray(softwareName) && softwareName.length > 0) ||
                  (typeof softwareName === 'string' && softwareName.trim())
                "
                vertical
                class="mobile-divider desktop-only"
              ></v-divider>
              <button
                @click="getAndOpenPDF"
                class="lesson-meta-item printer-button"
              >
                <div class="lesson-meta-item-icon">
                  <svg
                    width="20"
                    height="20"
                    viewBox="0 0 20 20"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                  >
                    <path
                      d="M15 15H17C18.1046 15 19 14.1046 19 13V9C19 7.89543 18.1046 7 17 7H3C1.89543 7 1 7.89543 1 9V13C1 14.1046 1.89543 15 3 15H5M7 19H13C14.1046 19 15 18.1046 15 17V13C15 11.8954 14.1046 11 13 11H7C5.89543 11 5 11.8954 5 13V17C5 18.1046 5.89543 19 7 19ZM15 7V3C15 1.89543 14.1046 1 13 1H7C5.89543 1 5 1.89543 5 3V7H15Z"
                      stroke="#b90057"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </div>
                <span class="meta-text meta-text-primary-pink">{{
                  buttonText
                }}</span>
              </button>
            </div>
          </div>
          <div class="generated-tabs">
            <span
              class="generated-tab"
              :class="{ active: activeTab === 'introduction' }"
              @click="setActiveTab('introduction')"
              >Introduction</span
            >
            <span
              class="generated-tab"
              :class="{ active: activeTab === 'opening' }"
              @click="setActiveTab('opening')"
              >Opening Activity</span
            >
            <span
              class="generated-tab"
              :class="{ active: activeTab === 'main-activity' }"
              @click="setActiveTab('main-activity')"
              >Main Activity</span
            >
            <span
              class="generated-tab"
              :class="{ active: activeTab === 'closing-activity' }"
              @click="setActiveTab('closing-activity')"
              >Closing Activity</span
            >
            <span
              class="generated-tab"
              :class="{ active: activeTab === 'assessment' }"
              @click="setActiveTab('assessment')"
              >Rubric</span
            >
          </div>
          <div class="content">
            <div
              v-if="activeTab === 'introduction'"
              v-html="introContent"
              class="content-padding"
            ></div>
            <div v-if="activeTab === 'opening'" class="content-padding">
              <LessonActivity
                :activity="worksheet_raw_dataOpening"
                :driveLink="driveLink"
                :slideId="lessonPackageId"
              />
            </div>
            <div v-if="activeTab === 'main-activity'" class="content-padding">
              <LessonActivity
                :activity="worksheet_raw_dataMain"
                :slideId="lessonPackageId"
                :driveLink="driveLink"
              />
            </div>
            <div
              v-if="activeTab === 'closing-activity'"
              class="content-padding"
            >
              <LessonActivity
                :activity="worksheet_raw_dataClosing"
                :slideId="lessonPackageId"
                :driveLink="driveLink"
              />
            </div>
            <div v-if="activeTab === 'assessment'" class="content-padding">
              <AssessmentView
                v-if="assessments.length"
                :assessments="assessments"
              />
              <div v-else v-html="assessmentContent"></div>
            </div>
          </div>

          <div class="resources-section">
            <h2 class="resources-title">Resources</h2>

            <div class="resources-buttons-wrapper">
              <ActionButton
                variant="primary"
                size="large"
                custom-class="btn-copy-slide"
                @click="isCopyModalOpen = true"
              >
                <template #icon-left>
                  <svg
                    width="24"
                    height="24"
                    viewBox="0 0 24 24"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    aria-hidden="true"
                  >
                    <path
                      d="M8 7C8 5.89543 8.89543 5 10 5H17C18.1046 5 19 5.89543 19 7V14C19 15.1046 18.1046 16 17 16H10C8.89543 16 8 15.1046 8 14V7Z"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                    <path
                      d="M5 9V17C5 18.1046 5.89543 19 7 19H15"
                      stroke="currentColor"
                      stroke-width="2"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                    />
                  </svg>
                </template>
                Copy Slide Deck to your Drive
              </ActionButton>
            </div>
          </div>
        </div>

        <!-- Content Sidebar -->
        <ContentSidebar
          :collapsed="!isExpanded"
          :show-mobile="showMobileSidebar"
          :is-edit="isEdit"
          :grade-name="gradeName"
          :subject-name="subjectName"
          :skillset-name="skillsetName"
          :mindset-name="mindsetName"
          :software-name="softwareName"
          :student-profiles="studentProfiles"
          :academic-standard="academicStandard"
          :future-ready-skillset="futureReadySKillset"
          @update:collapsed="handleSidebarCollapse"
          @update:showMobile="showMobileSidebar = $event"
          @discard="handleDiscard"
          @save="handleSave"
        />
      </template>
    </div>

    <!-- Mobile Sticky Footer -->
    <div
      v-if="!isBuildLoading && !hasError"
      class="mobile-sticky-footer mobile-only"
    >
      <button class="mobile-details-btn-full" @click="showMobileSidebar = true">
        View More Details
      </button>
    </div>

    <!-- upload slide deck -->
    <UploadSlideDeckModal
      v-model="isModalOpen"
      :drive="drive"
      @confirm="handleUploadConfirm"
      @close="handleUploadClose"
    />

    <!-- edit slide deck -->
    <EditSlideDeckModal
      v-model="isModalEdit"
      :drive-value="driveValue"
      @confirm="handleEditConfirm"
      @removeDrive="removeDrive"
      @close="handleEditClose"
    />

    <CopySlideModal
      v-model="isCopyModalOpen"
      :drive-link="driveLink"
      :is-loading="isLoading"
      @copy="copySlideToDrive"
      @close="handleCopyClose"
    />

    <ConfirmSlideDeckModal
      v-model="isModalConfirm"
      @confirm="handleSubmitConfirm"
      @cancel="handleConfirmCancel"
      @close="handleConfirmClose"
    />
    <PrivacyPolicyModal
      :isOpen="isPrivacyPolicyModalOpen"
      @close="isPrivacyPolicyModalOpen = false"
    />
    <TermsOfServiceModal
      :isOpen="isTermsOfServiceModalOpen"
      @close="isTermsOfServiceModalOpen = false"
    />
  </div>
</template>

<script setup>
/* eslint-disable no-unused-vars */
import {
  computed,
  defineEmits,
  defineExpose,
  onMounted,
  ref,
  watch,
} from "vue";
import { useRoute, useRouter } from "vue-router";
import { useStore } from "vuex";
import {
  default as LessonPackageService,
  default as lessonPackageService,
} from "../services/lessonPackageService";
import BaseInput from "./BaseInput.vue";
import ErrorGenerate from "./ErrorGenerate.vue";
import ContentSidebar from "./lesson-package/ContentSidebar.vue";
import LessonActivity from "./LessonActivity.vue";
import AssessmentView from "./lesson-library/AssessmentView.vue";
import PrivacyPolicyModal from "./modal/PrivacyPolicyModal.vue";
import TermsOfServiceModal from "./modal/TermsOfServiceModal.vue";
import UploadSlideDeckModal from "./lesson-package/UploadSlideDeckModal.vue";
import EditSlideDeckModal from "./lesson-package/EditSlideDeckModal.vue";
import CopySlideModal from "./lesson-package/CopySlideModal.vue";
import ConfirmSlideDeckModal from "./lesson-package/ConfirmSlideDeckModal.vue";
import BreadCrumbs from "./common/BreadCrumbs.vue";
import ActionButton from "./common/ActionButton.vue";
import { addTableHeaders } from "../utils/addTableHeaders";

const store = useStore();
const route = useRoute();
const router = useRouter();

// Add error state
const hasError = ref(false);
const errorTitle = ref("");
const errorMessage = ref("");
const showRetryButton = ref(false);

// Reactive state
const activeTab = ref("introduction");
const defaultContentWidth = ref(null);
const introContent = ref("");
const openingActivityContent = ref("");
const mainActivityContent = ref("");
const closingActivityContent = ref("");
const assessmentContent = ref("");
const assessments = ref([]);

const buildAssessmentsFromRubrics = (lessonPackageRaw) => {
  if (!lessonPackageRaw) return [];
  let parsed = lessonPackageRaw;
  if (typeof parsed === "string") {
    try {
      parsed = JSON.parse(parsed);
    } catch (e) {
      console.error("Failed to parse lesson_package_raw", e);
      return [];
    }
  }
  const rubrics = parsed?.rubrics;
  if (!rubrics || typeof rubrics !== "object") return [];
  const result = [];
  Object.keys(rubrics).forEach((key) => {
    const entries = Array.isArray(rubrics[key]) ? rubrics[key] : [];
    entries.forEach((r, idx) => {
      result.push({
        id: `${key}-${idx}`,
        title: r.criteria || "",
        items: [
          { bullets: r.emerging || [] },
          { bullets: r.developing || [] },
          { bullets: r.proficient || [] },
        ],
      });
    });
  });
  return result;
};
const title = ref("");
const softwareName = ref(null);
const gradeName = ref(null);
const skillsetName = ref(null);
const mindsetName = ref(null);
const studentProfiles = ref([]);
const futureReadySKillset = ref(null);
const academicStandard = ref(null);
const subjectName = ref(null);
const isPrivacyPolicyModalOpen = ref(false);
const isTermsOfServiceModalOpen = ref(false);

const resource = ref(null);
const pageMeta = ref(null);
const buttonText = ref("Print as a PDF");
const contentId = ref(null);
const lessonPackageId = ref(null);
const teacherModeId = ref(null);
const isExpanded = ref(false);
const isEdit = ref(false);
const showMobileSidebar = ref(false);
const isModalOpen = ref(false);
const isCopyModalOpen = ref(false);
const isModalEdit = ref(false);
const drive = ref(null);
const driveLink = ref("");
const isLoading = ref(false);
const isBuildLoading = ref(true);

const worksheet_raw_dataOpening = ref([]);
const worksheet_raw_dataMain = ref([]);
const worksheet_raw_dataClosing = ref([]);
const isModalConfirm = ref(false);

const user = computed(() => store.state.user);

const shouldShowBreadcrumbs = computed(() => {
  const routeName = route.name;
  return routeName === "MyLessonPackagePage";
});

const breadcrumbItems = computed(() => {
  const items = [];

  items.push({
    label: "My Resources",
    to: { name: "MyResourcesPage" },
    active: false,
  });

  items.push({
    label: "Edtech Studio",
    to: {
      name: "MyResourcesPage",
      query: {
        tab: "user-generated-resources",
      },
    },
    active: false,
  });

  if (title.value) {
    items.push({
      label: title.value,
      to: null,
      active: true,
    });
  }

  return items;
});

const driveValue = localStorage.getItem("drive");
const lessonStorage = localStorage.getItem("lesson-storage");

const removeDrive = () => {
  localStorage.removeItem("drive");
  window.location.reload();
};

// Emit events
const emit = defineEmits(["loading", "error"]);

// Methods
const setDefaultContentWidth = () => {
  const contentElement = document.querySelector(".content");
  if (contentElement) {
    const marginSpace = 100;
    defaultContentWidth.value = window.innerWidth - marginSpace;
  }
};

const fetchPageMeta = () => {
  pageMeta.value = route.meta.page;
};

const decryptDataFromURL = () => {
  const encryptedString = route.query.data;
  if (!encryptedString) {
    router.push({ path: "/", query: { key: "reset" } });
    return;
  }
  const decryptedString = decodeURIComponent(encryptedString);
  return JSON.parse(decryptedString);
};

const setActiveTab = (tabName) => {
  activeTab.value = tabName;
};

const handleUploadConfirm = (driveValue) => {
  drive.value = driveValue;
  localStorage.setItem("drive", driveValue);
  isModalOpen.value = false;
  isModalConfirm.value = true;
};

const handleUploadClose = () => {
  isModalOpen.value = false;
};

const handleEditConfirm = (driveValue) => {
  drive.value = driveValue;
  localStorage.setItem("drive", driveValue);
  isModalEdit.value = false;
  window.location.reload();
};

const handleEditClose = () => {
  isModalEdit.value = false;
};

const handleCopyClose = () => {
  isCopyModalOpen.value = false;
};

const handleConfirmCancel = () => {
  isModalConfirm.value = false;
};

const handleConfirmClose = () => {
  isModalConfirm.value = false;
};

const handleSubmitConfirm = () => {
  isModalConfirm.value = false;
  window.location.reload();
};

const findSectionByTitle = (content, title) => {
  const keys = Object.keys(content);
  for (const key of keys) {
    const section = content[key];
    if (section.title === title) {
      return section;
    }
  }
  return null;
};

const findSectionContent = (section) => {
  if (!section) return "";
  const keys = Object.keys(section.content);
  for (const key of keys) {
    const content = section.content[key];
    return content.content;
  }
};

const toggleSidebar = () => {
  isExpanded.value = !isExpanded.value;
  isEdit.value = false;
};

const toggleSidebarEdit = () => {
  isExpanded.value = !isExpanded.value;
  isEdit.value = true;
};

const handleSidebarCollapse = (collapsed) => {
  isExpanded.value = !collapsed;
  if (collapsed) {
    isEdit.value = false;
  }
};

const handleDiscard = () => {
  // Handle discard action - you can add your logic here
  isEdit.value = false;
  console.log("Changes discarded");
};

const handleSave = () => {
  // Handle save action - you can add your logic here
  isEdit.value = false;
  console.log("Changes saved");
};

const buildLessonPackage = async (data, attempt = 0) => {
  emit("loading", true);
  isBuildLoading.value = true;
  hasError.value = false;
  showRetryButton.value = true; // Show retry for build lesson package
  try {
    const response = await LessonPackageService.buildLessonPackage(
      user.value?.email,
      data
    );
    if (response.status === 200 && response.data.data) {
      const content = response.data.data.lesson_package.content;
      title.value = response.data.data.lesson_package.title;
      resource.value = response.data.data.resource;
      contentId.value = response.data.data.lesson_package.id;
      lessonPackageId.value = contentId.value;

      introContent.value = addTableHeaders(
        findSectionContent(findSectionByTitle(content, "Introduction"))
      );
      openingActivityContent.value = findSectionContent(
        findSectionByTitle(content, "Opening Activity")
      );
      mainActivityContent.value = findSectionContent(
        findSectionByTitle(content, "Main Activity")
      );
      closingActivityContent.value = findSectionContent(
        findSectionByTitle(content, "Closing Activity")
      );
      worksheet_raw_dataOpening.value =
        response.data.data.lesson_package.worksheet_raw_data.openingActivity;
      worksheet_raw_dataMain.value =
        response.data.data.lesson_package.worksheet_raw_data.mainActivity;
      worksheet_raw_dataClosing.value =
        response.data.data.lesson_package.worksheet_raw_data.closingActivity;
      driveLink.value = response.data.data.lesson_package.drive_link;

      assessmentContent.value = addTableHeaders(
        findSectionContent(findSectionByTitle(content, "Assessment"))
      );
      assessments.value = buildAssessmentsFromRubrics(
        response.data.data.lesson_package.lesson_package_raw
      );
      // Log nilai setelah assignment
      console.log(
        "worksheet_raw_dataOpening after assignment:",
        worksheet_raw_dataOpening.value
      );
      const selectedData = response.data.data.resource.selected_data;
      softwareName.value =
        response.data.data.resource.payload.software ||
        response.data.data.resource.raw_data[selectedData - 1]?.core?.software;

      teacherModeId.value = response.data.data.resource.slides_id;

      gradeName.value = response.data.data.resource.grade;
      softwareName.value = response.data.data.resource.software;
      skillsetName.value = response.data.data.resource.skillset;
      mindsetName.value = response.data.data.resource.mindset;
      studentProfiles.value = response.data.data.resource.student_profile || [];

      futureReadySKillset.value =
        response.data.data.resource.future_ready_skillset;
      subjectName.value = response.data.data.resource.subject;
      academicStandard.value = response.data.data.resource.academic_standard;

      isBuildLoading.value = false;
      emit("loading", false);
    } else if (response.status === 200) {
      // do nothing
    } else {
      console.log("### reload 1");
      // window.location.reload();
      isBuildLoading.value = false;
      hasError.value = true;
      errorTitle.value = "Failed to Build Lesson Package";
      let errorMsg =
        "We encountered an error while building your lesson package.";
      if (response.data && response.data.message) {
        errorMsg += "\n\nTechnical details:\n" + response.data.message;
      }
      errorMessage.value = errorMsg;
      emit("error", {
        title: errorTitle.value,
        message: errorMsg,
      });
      throw new Error(`Request failed with status ${response.status}`);
    }
  } catch (error) {
    console.error(error);

    hasError.value = true;
    errorTitle.value = "Failed to Build Lesson Package";
    let errorMsg =
      "We encountered an error while building your lesson package.";
    if (error.response?.data?.message) {
      errorMsg += "\n\nTechnical details:\n" + error.response.data.message;
    } else if (error.message) {
      errorMsg += "\n\nTechnical details:\n" + error.message;
    }
    errorMessage.value = errorMsg;
    emit("error", {
      title: errorTitle.value,
      message: errorMsg,
    });

    if (error.message === "Network Error") {
      console.log("### reload 2");
      // window.location.reload();
      return;
    }
    if (error?.code === "ERR_NETWORK" && route.query.retry !== "1") {
      console.log("Request timeout on catch", { attempt });
      const baseUrl = window.location.origin;
      const path = `${baseUrl}${route.fullPath}&retry=1`;
      location.href = path;
    }
  } finally {
    // emit("loading", false);
    // isBuildLoading.value = false;
  }
};

const fetchDataLessonPackage = async (id) => {
  emit("loading", true);
  hasError.value = false;
  showRetryButton.value = false; // Don't show retry for fetch lesson package
  isBuildLoading.value = true;
  try {
    const response = await LessonPackageService.getLessonPackage(
      user.value?.email,
      id
    );
    if (response.status === 200) {
      const content = response.data.data.lesson_package.content;
      title.value = response.data.data.lesson_package.title;
      resource.value = response.data.data.resource;
      contentId.value = response.data.data.lesson_package.id;

      introContent.value = addTableHeaders(
        findSectionContent(findSectionByTitle(content, "Introduction"))
      );
      openingActivityContent.value = findSectionContent(
        findSectionByTitle(content, "Opening Activity")
      );
      mainActivityContent.value = findSectionContent(
        findSectionByTitle(content, "Main Activity")
      );
      closingActivityContent.value = findSectionContent(
        findSectionByTitle(content, "Closing Activity")
      );

      worksheet_raw_dataOpening.value =
        response.data.data.lesson_package.worksheet_raw_data.openingActivity;
      worksheet_raw_dataMain.value =
        response.data.data.lesson_package.worksheet_raw_data.mainActivity;
      worksheet_raw_dataClosing.value =
        response.data.data.lesson_package.worksheet_raw_data.closingActivity;
      driveLink.value = response.data.data.lesson_package.drive_link;

      assessmentContent.value = addTableHeaders(
        findSectionContent(findSectionByTitle(content, "Assessment"))
      );
      assessments.value = buildAssessmentsFromRubrics(
        response.data.data.lesson_package.lesson_package_raw
      );
      const selectedData = response.data.data.resource.selected_data;
      softwareName.value =
        response.data.data.resource.payload.software ||
        response.data.data.resource.raw_data[selectedData - 1]?.core?.software;

      teacherModeId.value = response.data.data.resource.slides_id;

      gradeName.value = response.data.data.resource.grade;
      softwareName.value = response.data.data.resource.software;
      skillsetName.value = response.data.data.resource.skillset;
      mindsetName.value = response.data.data.resource.mindset;
      studentProfiles.value = response.data.data.resource.student_profile || [];

      futureReadySKillset.value =
        response.data.data.resource.future_ready_skillset;
      subjectName.value = response.data.data.resource.subject;
      academicStandard.value = response.data.data.resource.academic_standard;

      if (
        response.data?.data?.resource &&
        response?.data?.data?.lesson_package
      ) {
        emit("loading", false);
      }
    } else {
      console.log("### reload 3");
      // window.location.reload();
      hasError.value = true;
      errorTitle.value = "Failed to Load Lesson Package";
      let errorMsg = "We couldn't load the lesson package.";
      if (response.data && response.data.message) {
        errorMsg += "\n\nTechnical details:\n" + response.data.message;
      }
      errorMessage.value = errorMsg;
      emit("error", {
        title: errorTitle.value,
        message: errorMsg,
      });
      throw new Error(`Request failed with status ${response.status}`);
    }
  } catch (error) {
    console.log("### reload 4");
    // window.location.reload();
    console.error(error);

    hasError.value = true;
    errorTitle.value = "Failed to Load Lesson Package";
    let errorMsg = "We couldn't load the lesson package.";
    if (error.response?.data?.message) {
      errorMsg += "\n\nTechnical details:\n" + error.response.data.message;
    } else if (error.message) {
      errorMsg += "\n\nTechnical details:\n" + error.message;
    }
    errorMessage.value = errorMsg;
    emit("error", {
      title: errorTitle.value,
      message: errorMsg,
    });

    if (error.message === "Network Error") {
      console.log("### reload 5");
      // window.location.reload();
      return;
    }
  } finally {
    emit("loading", false);
    isBuildLoading.value = false;
  }
};

const getAndOpenPDF = async () => {
  buttonText.value = "Downloading...";
  try {
    const response = await lessonPackageService.getPDF(lessonPackageId.value);
    if (response.status === 200) {
      const blob = new Blob([response.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${title.value}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } else {
      console.error("Error fetching PDF:", response.status);
    }
  } catch (error) {
    console.error("Error fetching PDF:", error);
  } finally {
    buttonText.value = "Print as a PDF";
  }
};

const openTeacherMode = () => {
  window.open(
    `https://slides.eddy4teachers.com/${teacherModeId.value}`,
    "_blank"
  );
};

const copySlideToDrive = () => {
  isLoading.value = true;
  lessonPackageService
    .copySlideToDrive(user.value?.email, lessonPackageId.value)
    .then((response) => {
      const data = response.data?.data;
      console.log(data, "data");
      window.open(data.url);
      if (data?.url?.includes("drive.google.com")) {
        window.location.reload();
      }
    })
    .catch((err) => {
      console.error(err, "Error");
    })
    .finally(() => {
      isLoading.value = false;
    });
};

// Lifecycle hooks
onMounted(() => {
  setDefaultContentWidth();
  fetchPageMeta();

  window.addEventListener("resize", setDefaultContentWidth);
  lessonPackageId.value = route.params.id;

  if (lessonPackageId.value && user.value?.email) {
    fetchDataLessonPackage(lessonPackageId.value);
  } else {
    if (user.value?.email) {
      buildLessonPackage(decryptDataFromURL());
    }
  }
});

// Add retry handler
const handleRetry = () => {
  if (lessonPackageId.value && user.value?.email) {
    fetchDataLessonPackage(lessonPackageId.value);
  } else {
    if (user.value?.email) {
      buildLessonPackage(decryptDataFromURL());
    }
  }
};

watch(user, (newValue, oldValue) => {
  if (lessonPackageId.value && newValue?.email) {
    fetchDataLessonPackage(lessonPackageId.value);
  } else {
    if (newValue?.email) {
      buildLessonPackage(decryptDataFromURL());
    }
  }
});

defineExpose({
  handleRetry,
  hasError,
});
</script>

<style scoped>
.main-container {
  display: flex;
  margin-bottom: 5em;
}

.mobile-only {
  display: none !important;
}

.content-section {
  margin-left: 50px;
  margin-right: 20px;
  width: 100%;
}

:deep(ul),
:deep(p),
:deep(td) {
  color: var(--black-body-text);
}

:deep(.title) {
  font-style: normal;
  font-weight: 500;
  line-height: 32px;
  color: var(--black-body-text);
  font-size: var(--fs-large);
}

:deep(.standard-text) {
  color: var(--black-body-text);
  font-size: var(--fs-medium);
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: 0.08px;
}

.header-wrapper {
  display: flex;
  gap: 5px;
  justify-content: space-between;
  margin-top: 16px;
}

.lesson-meta-container {
  display: flex;
  gap: 10px;
  align-items: end;
  margin: 0.6em 0em 1.2em 0em;
}

.lesson-meta {
  display: flex;
  gap: 16px;
  color: #777;
  align-items: center;
}

.lesson-meta-item {
  display: flex;
  align-items: center;
  gap: 8px;
  color: var(--primary-blue);
}

.meta-text {
  font-family: "League Spartan", sans-serif;
  font-weight: 500;
  font-size: var(--fs-small);
  white-space: nowrap;
}

.meta-text-blue {
  color: var(--primary-blue);
}

.meta-text-pink {
  color: #e00069;
}

.meta-text-primary-pink {
  color: #b90057;
}

.lesson-meta-item-icon {
  width: 24px;
  height: 24px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.lesson-meta-item-icon img,
.lesson-meta-item-icon svg {
  width: 100%;
  height: 100%;
}

.printer-button {
  border-radius: 100px;
  background: var(--white-hover-1, #f2f3fb);
  padding: 4px 8px;
  cursor: pointer;
  border: none;
}

.desktop-only {
  display: flex !important;
}

.content-padding {
  padding: 0.5rem 0;
}

/* Generated HTML table styles — matches Figma design */
.content-padding :deep(table),
.content :deep(table) {
  display: table !important;
  width: 100% !important;
  max-width: 100% !important;
  border-collapse: separate !important;
  border-spacing: 0 !important;
  border-radius: 16px !important;
  overflow: hidden !important;
  margin-bottom: 1rem !important;
}

.content-padding :deep(table tr:first-child td),
.content-padding :deep(table tr:first-child th),
.content :deep(table tr:first-child td),
.content :deep(table tr:first-child th) {
  background-color: var(--primary-blue) !important;
  color: var(--white) !important;
  font-size: var(--fs-large) !important;
  font-weight: 500 !important;
  line-height: 28px !important;
  letter-spacing: 0 !important;
  padding: 20px 10px !important;
  border: none !important;
  border-bottom: 1px solid var(--neutral-300) !important;
  text-align: left !important;
}

.content-padding :deep(table tr:first-child td .table-label),
.content :deep(table tr:first-child td .table-label),
.content-padding :deep(table tr:first-child th .table-label),
.content :deep(table tr:first-child th .table-label) {
  color: var(--white) !important;
  font-weight: 500 !important;
}

.content-padding :deep(table td),
.content-padding :deep(table th),
.content :deep(table td),
.content :deep(table th) {
  min-width: 120px !important;
  white-space: normal !important;
  padding: 20px 10px !important;
  border: none !important;
  border-bottom: 1px solid var(--neutral-300) !important;
  font-size: var(--fs-medium) !important;
  font-weight: 400 !important;
  line-height: 24px !important;
  letter-spacing: 0.08px !important;
  color: var(--black-body-text) !important;
  vertical-align: top !important;
  background-color: transparent !important;
}

/* Header row cells must override the general td/th styles */
.content-padding :deep(table tr:first-child td),
.content-padding :deep(table tr:first-child th),
.content :deep(table tr:first-child td),
.content :deep(table tr:first-child th) {
  background-color: var(--primary-blue) !important;
  color: var(--white) !important;
  font-size: var(--fs-large) !important;
  font-weight: 500 !important;
  line-height: 28px !important;
}

/* Left and right border on outer columns */
.content-padding :deep(table td:first-child),
.content-padding :deep(table th:first-child),
.content :deep(table td:first-child),
.content :deep(table th:first-child) {
  border-left: 1px solid var(--neutral-300) !important;
}

.content-padding :deep(table td:last-child),
.content-padding :deep(table th:last-child),
.content :deep(table td:last-child),
.content :deep(table th:last-child) {
  border-right: 1px solid var(--neutral-300) !important;
}

/* Top border on header row */
.content-padding :deep(table tr:first-child td),
.content-padding :deep(table tr:first-child th),
.content :deep(table tr:first-child td),
.content :deep(table tr:first-child th) {
  border-top: 1px solid var(--neutral-300) !important;
}

/* Responsive table wrapper */
@media (max-width: 768px) {
  .content-padding :deep(table),
  .content :deep(table) {
    display: block !important;
    overflow-x: auto !important;
    -webkit-overflow-scrolling: touch !important;
  }
}

.content :deep(th.table-label) {
  color: var(--white) !important;
}

.resources-section {
  margin-top: 50px;
}

.resources-title {
  font-size: var(--fs-xl);
  font-weight: 600;
  color: var(--black-body-text);
  margin-top: 15px;
}

.resources-buttons-wrapper {
  width: fit-content;
  margin-bottom: 50px;
}

.btn-copy-slide.action-button--large {
  gap: 8px;
  padding: 10px 16px;
  min-height: 48px;
  border-color: #003d6a;
  border-radius: 12px;
  background-color: #003d6a;
  color: var(--white);
  text-transform: none;
}

.btn-copy-slide:deep(.action-button__content) {
  gap: 8px;
  justify-content: start;
}

.btn-copy-slide:deep(.action-button__text) {
  font-size: 20px;
  font-weight: 700;
  line-height: 28px;
  letter-spacing: 0.03px;
}

.btn-copy-slide:deep(.action-button__icon svg) {
  width: 24px;
  height: 24px;
}

.links {
  cursor: pointer;
  color: #0056b3;
  text-decoration: underline;
}

.container {
  width: 80%;
  margin: 0 auto;
}

.header {
  font-size: 36px;
  font-style: normal;
  font-weight: 700;
  line-height: 44px;
  color: var(--primary-blue);
  margin: 0;
}

.generated-tabs {
  display: flex;
  gap: 10px;
  margin-bottom: 16px;
}

.generated-tab {
  cursor: pointer;
  display: flex;
  padding: 16px;
  justify-content: center;
  align-items: center;
  color: var(--black-body-text);
  font-size: var(--fs-big);
  font-style: normal;
  font-weight: 400;
  line-height: 24px; /* 150% */
  letter-spacing: 0.024px;
  border-bottom: 1px solid var(--neutral-700);
}

.generated-tab.active {
  border-bottom: 3px solid var(--primary-pink, #ff5a5f);
  color: var(--primary-pink, #ff5a5f);
  font-weight: bold;
}

.generated-tab:hover {
  color: var(--primary-pink, #ff5a5f);
  border-bottom-color: var(--primary-pink, #ff5a5f);
}

.sub-header {
  font-size: 1.3em;
  font-weight: 500;
}

.checkbox {
  width: 17px;
  height: 17px;
  margin-top: 0.1rem;
}

.content {
  font-family: "League Spartan";
  font-size: var(--fs-medium);
}

ul {
  padding: 0;
}

li {
  margin: 10px 0;
}

.activity-table {
  width: 60%;
  border-collapse: collapse;
}

.activity-table th,
.activity-table td {
  border: 1px solid #ddd;
  padding: 8px;
  text-align: left;
}

.activity-table th {
  background-color: #f8f8f8;
}

.category-label {
  font-size: 1.2em;
  font-weight: 700;
}

.category-chip {
  font-size: 0.7em;
  padding: 0.1em;
  border-radius: 2px;
}

.category-chip-child {
  background-color: rgb(234, 236, 240);
  padding: 0.3em;
  border-radius: 3px;
}

.standard-text {
  font-size: 1rem;
  font-weight: 400;
}

.button-blue {
  width: max-content;
  padding: 0.625rem 1.25rem;
  border-radius: 1rem;
  background-color: var(--primary-blue);
  color: var(--white);
  line-height: 1.25rem;
  text-align: center;
  text-decoration: none;
}

/* Override embedded API styles for slide containers */
:deep(.api-slide-container) {
  display: flex !important;
  align-items: flex-start !important;
  gap: 16px !important;
  padding: 8px !important;
  background-color: var(--white-hover-1) !important;
  border-radius: 8px !important;
}

:deep(.api-slide-picture) {
  width: 469px !important;
  height: 265px !important;
  border-radius: 0.75rem !important;
  overflow: hidden !important;
  flex-shrink: 0 !important;
}

:deep(.api-slide-picture > *) {
  width: 100% !important;
  height: 100% !important;
  border-radius: 0.75rem !important;
  object-fit: cover !important;
}

:deep(.api-slide-number) {
  margin-top: 0.5rem !important;
  font-size: var(--fs-medium) !important;
  font-weight: 500 !important;
  line-height: 24px !important;
  letter-spacing: 0.08px !important;
  color: var(--black-body-text) !important;
}

:deep(.api-slide-intro) {
  flex: 1 0 0 !important;
  min-height: 1px !important;
  min-width: 1px !important;
  margin-left: 0 !important;
  padding-left: 16px !important;
  border-left: 2px solid var(--neutral-100) !important;
  font-size: var(--fs-big) !important;
  font-weight: 400 !important;
  line-height: 28px !important;
  letter-spacing: 0.15px !important;
  color: var(--black-body-text) !important;
  align-self: center !important;
}

:deep(.api-slide-divider) {
  background-color: var(--neutral-100) !important;
  margin: 1.5rem 0 !important;
}

@media (max-width: 768px) {
  :deep(.api-slide-container) {
    flex-direction: column !important;
    gap: 12px !important;
  }

  :deep(.api-slide-picture) {
    width: 100% !important;
    height: auto !important;
    flex-shrink: 1 !important;
    overflow: visible !important;
  }

  :deep(.api-slide-picture > *) {
    width: 100% !important;
    height: auto !important;
    aspect-ratio: 469 / 265 !important;
    object-fit: cover !important;
  }

  :deep(.api-slide-intro) {
    flex: none !important;
    border-left: none !important;
    padding-left: 0 !important;
    margin-left: 0 !important;
    font-size: var(--fs-small) !important;
    line-height: 20px !important;
    text-align: left !important;
    align-self: flex-start !important;
    min-height: unset !important;
  }
}

@media (max-width: 768px) {
  .mobile-only {
    display: flex !important;
  }

  .desktop-only {
    display: none !important;
  }

  .header-wrapper {
    border-bottom: 1px solid var(--neutral-100);
  }

  .main-container {
    flex-direction: column;
    margin-bottom: 80px;
  }

  .content-section {
    margin-left: 15px;
    margin-right: 15px;
    width: auto;
  }

  .header {
    font-size: var(--fs-xxl, 28px);
    line-height: 1.2;
    margin: 0;
    max-width: 100%;
  }

  .lesson-meta-container {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    margin-top: 0;
  }

  .lesson-meta {
    flex-direction: column;
    align-items: flex-start;
    gap: 15px;
    margin-top: 10px;
  }

  .lesson-meta-item {
    min-height: 44px;
  }

  .generated-tabs {
    overflow-x: auto;
    white-space: nowrap;
    padding-bottom: 15px;
    gap: 15px;
    margin-bottom: 20px;
    -webkit-overflow-scrolling: touch;
    scrollbar-width: none;
    -ms-overflow-style: none;
  }

  .generated-tabs::-webkit-scrollbar {
    display: none;
  }

  .generated-tab {
    display: inline-block;
    min-width: fit-content;
    padding: 10px 15px;
    font-size: var(--fs-big);
    border-bottom: 1px solid var(--neutral-700);
  }

  .resources-section {
    margin-top: 20px;
  }

  .resources-title {
    font-size: 1.4rem;
  }

  .resources-buttons-wrapper {
    width: 100%;
  }

  .btn-copy-slide.action-button--large {
    width: 100%;
    justify-content: center;
  }

  .mobile-sticky-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    padding: 16px;
    background-color: white;
    box-shadow: 0px -4px 15px rgba(0, 0, 0, 0.05);
    z-index: 100;
    display: flex;
    justify-content: center;
    border-top: 1px solid var(--neutral-300, #e2e8f0);
  }

  .mobile-details-btn-full {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    border: 1px solid var(--primary-blue);
    border-radius: 12px;
    color: var(--primary-blue);
    font-family: var(--font-family-primary);
    font-weight: var(--font-weight-bold);
    font-size: var(--fs-title-big, 16px);
    width: 100%;
    padding: 10px 16px;
    min-height: 48px;
    background: transparent;
    cursor: pointer;
  }
}

@media (max-width: 480px) {
  .content-section {
    margin-left: 10px;
    margin-right: 10px;
  }

  .generated-tabs {
    gap: 10px;
    margin-bottom: 15px;
  }

  .generated-tab {
    padding: 8px 12px;
    font-size: var(--fs-medium, 0.875rem);
  }

  .lesson-meta {
    flex-direction: row;
    flex-wrap: wrap;
    align-items: center;
  }

  .lesson-meta-item {
    font-size: 0.9rem;
  }

  .btn-copy-slide.action-button--large {
    padding: 10px 12px;
  }
}
</style>
